"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface FloatingActionButtonProps {
  showScrollTop?: boolean;
  showChat?: boolean;
  className?: string;
}

export default function FloatingActionButton({
  showScrollTop = true,
  showChat = true,
  className,
}: FloatingActionButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showChatButton, setShowChatButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    // Show chat button after a delay
    const chatTimer = setTimeout(() => {
      setShowChatButton(true);
    }, 2000);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(chatTimer);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div
      className={cn(
        "fixed bottom-8 right-6 md:right-8 z-50 flex flex-col gap-3",
        className
      )}
    >
      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="w-12 h-12 bg-[var(--blue-500)] text-white rounded-full shadow-sm hover:bg-[var(--blue-800)] transition-colors flex items-center justify-center"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Quick Links */}
      <AnimatePresence>
        {showChat && showChatButton && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
          >
            <div className="flex flex-col gap-3">
              <motion.a
                href="https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full shadow-sm flex items-center justify-center transition-shadow"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                aria-label="LINE 聯絡"
              >
                <Image
                  src="/icon/line.png"
                  alt="LINE"
                  width={150}
                  height={150}
                  className="w-7 h-7 md:w-9 md:h-9 object-contain"
                />
              </motion.a>
                            <motion.a
                href="https://m.me/305575275979962"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full shadow-sm flex items-center justify-center transition-shadow"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Facebook Messenger 聯絡"
              >
                <Image
                  src="/icon/msg.png"
                  alt="Facebook Messenger"
                  width={150}
                  height={150}
                  className="w-7 h-7 md:w-9 md:h-9 object-contain"
                />
              </motion.a>
              <motion.a
                href="https://workspace.google.com/marketplace/app/%E5%BF%AB%E5%AE%A2_%E7%AC%AC%E4%B8%89%E6%96%B9%E4%B8%B2%E6%8E%A5%E5%B7%A5%E5%85%B7/663018989708"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full shadow-sm flex items-center justify-center transition-shadow"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Google Sheets 擴充"
              >
                <Image
                  src="/icon/sheet.png"
                  alt="Google Sheets"
                  width={150}
                  height={150}
                  className="w-7 h-7 md:w-9 md:h-9 object-contain"
                />
              </motion.a>

              <motion.a
                href="https://chromewebstore.google.com/detail/dagdchpdhalojgcfnhbnmkcbjoggbclb"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full shadow-sm flex items-center justify-center transition-shadow"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Chrome 擴充"
              >
                <Image
                  src="/icon/chrome.png"
                  alt="Google Chrome"
                  width={150}
                  height={150}
                  className="w-7 h-7 md:w-9 md:h-9 object-contain"
                />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
